// Originally forked from: https://github.com/colinaut/alpinejs-plugin-simple-validate

// Named attributes used multiple times
const DATA_ERROR = "data-error";
const DATA_ERROR_MSG = `${DATA_ERROR}-msg`;
const ERROR_MSG_CLASS = "clio-ui-simple-form-error";
const PLUGIN_NAME = "validate";

// These are just for better minification
const REQUIRED = "required";
const INPUT = "input";
const CHECKBOX = "checkbox";
const RADIO = "radio";
const GROUP = "group";
const FORM = "form";
const FIELDSET = "fieldset";
const notType = (type) => `:not([type="${type}"])`;
const FIELD_SELECTOR = `${INPUT}${notType("search")}${notType(
  "reset"
)}${notType("submit")},select,textarea`;
const HIDDEN = "hidden";

// Helper functions
const isHtmlElement = (el, type?) => {
  const isInstanceOfHTML = el instanceof HTMLElement;
  return type ? isInstanceOfHTML && el.matches(type) : isInstanceOfHTML;
};

const includes = (array, string) => {
  return Array.isArray(array) && array.includes(string);
};

const addEvent = (el, event, callback) => {
  return el.addEventListener(event, callback);
};

const getAttr = (el, attr) => {
  if (isHtmlElement(el)) {
    return el.getAttribute(attr);
  }
};

const setAttr = (el, attr, value: any = "") => {
  return el.setAttribute(attr, value);
};

const getEl = (el) => {
  return isHtmlElement(el)
    ? el
    : document.getElementById(el) || document.querySelector(`[name ="${el}"]`);
};

const getForm = (el) => {
  return el && el.closest(FORM);
};

const getName = (el) => {
  return getAttr(el, "name") || getAttr(el, "id");
};

const cleanText = (str) => {
  return String(str).trim();
};

const getData = (strOrEl) => {
  const el = getEl(strOrEl);
  let data = formData.get(getForm(el));
  if (!data) return false;
  if (isHtmlElement(el, FORM)) return Object.values(data);
  if (isHtmlElement(el, FIELDSET))
    return Object.values(data).filter((val: any) => val.set === el);
  if (isHtmlElement(el, FIELD_SELECTOR)) return data[getName(el)];
};

const isCheckboxWrapper = (el) => {
  return el?.parentNode.classList.contains("clio-ui-simple-form-checkbox");
};

const isCollectionWrapper = (el) => {
  return el?.closest(".clio-ui-simple-form-collection-wrapper") !== null;
};

const isAutoCompleteWrapper = (el) => {
  return el?.closest(".clio-ui-autocomplete") !== null;
};

const isComboboxWrapper = (el) => {
  return el?.closest(".clio-ui-combobox") !== null;
};

const isMultiSelectWrapper = (el) => {
  return el?.closest(".clio-ui-multi-select") !== null;
};

const findSiblingWithClass = (element, className) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    if (sibling.classList.contains(className)) {
      return sibling;
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

// Validators
const dateFormats = ["mmddyyyy", "ddmmyyyy", "yyyymmdd"];

const yearLastDateRegex = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/;
const yearFirstDateRegex = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/;

function isDate(str, format = dateFormats[2]) {
  // format defaults to yyyymmdd
  const dateArray = str.split(/[-/.]/);
  const formatIndexInArray = dateFormats.indexOf(format);

  let mm, dd, yyyy;

  if (yearLastDateRegex.test(str)) {
    if (formatIndexInArray === 0) [mm, dd, yyyy] = dateArray;
    if (formatIndexInArray === 1) [dd, mm, yyyy] = dateArray;
  } else if (yearFirstDateRegex.test(str) && formatIndexInArray === 2) {
    [yyyy, mm, dd] = dateArray;
  }

  const isoFormattedStr = `${yyyy}-${mm}-${dd}`;
  const date = new Date(isoFormattedStr);

  const timestamp = date.getTime();

  if (typeof timestamp !== "number" || Number.isNaN(timestamp)) return false;

  return date.toISOString().startsWith(isoFormattedStr);
}

function cleanupForm(form) {
  // Disconnect the observer if it exists
  const observer = formObservers.get(form);
  if (observer) {
    observer.disconnect();
    formObservers.delete(form);
  }
}

// Validation Reactive Data Store
const formData = new WeakMap<object, any>();

// non-reactive variable for modifiers on a per form basis
const formModifiers = new WeakMap();
const formObservers = new WeakMap();

// Validation Functions
const validate: Record<string, any> = {};

validate.email = (str) => {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    cleanText(str)
  );
};

validate.tel = (str) => {
  return /^((\+|0)\d{1,4})?[-\s.]?[-\s.]?\d[-\s.]?\d[-\s.]?\d[-\s.]?\d[-\s.]?\d[-\s.]?\d[-\s.]?\d[-\s.]?\d[-\s.]?(\d{1,2})$/.test(
    cleanText(str)
  );
};

validate.website = (str) => {
  return /^(https?:\/\/)?(www\.)?([-a-zA-Z0-9@:%._+~#=]+(-?[a-zA-Z0-9])*\.)+[\w]{2,}(\/\S*)?$/.test(
    cleanText(str)
  );
};

validate.url = (str) => {
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()!@:%_+.~#?&/=]*)/.test(
    cleanText(str)
  );
};

validate.number = (str) => {
  return !isNaN(parseFloat(str)) && isFinite(str);
};

validate.integer = (str) => {
  return validate.number(str) && Number.isInteger(Number(str));
};

validate.wholenumber = (str) => {
  return validate.integer(str) && Number(str) > 0;
};

validate.date = (str) => {
  return isDate(str);
};

dateFormats.forEach((format) => {
  validate.date[format] = (str) => isDate(str, format);
});

export default function (Alpine) {
  /* -------------------------------------------------------------------------- */
  /*                           formData function                                */
  /* -------------------------------------------------------------------------- */

  function updateFormData(field, data?, triggerErrorMsg?) {
    const form = getForm(field);
    const name = getName(field);

    // only add data if has form and field name
    if (form && name) {
      // make sure form object exists
      if (!formData.has(form)) {
        formData.set(form, Alpine.reactive({}));
      }
      let tempData = formData.get(form);

      // Add any data from formData, then name, node, and value if it's not being passed along
      data = {
        ...tempData[name],
        name: name,
        node: field,
        value: field.value,
        ...data,
      };

      // update required if not included
      data.required =
        data.required ||
        includes(data.mods, REQUIRED) ||
        includes(data.mods, GROUP) ||
        field.hasAttribute(REQUIRED);

      const value = data.value;

      // run basic browser validity
      let valid = field.checkValidity();

      // If checkbox/radio then assume it's a group so update array and string value based on checked
      if (includes([CHECKBOX, RADIO], field.type)) {
        if (data.required) valid = field.checked;
        // data.array acts as a store of current selected values
        let tempArray = data.array || [];

        if (field.type === CHECKBOX) {
          if (field.checked && !tempArray.includes(value))
            tempArray.push(value);
          if (!field.checked)
            tempArray = tempArray.filter((val) => val !== value);
        }

        // Radio buttons only can select one so max array is 1.
        if (field.type === RADIO && field.checked) tempArray = [value];

        // update with revised array
        data.array = tempArray;
        // update value with string of array items
        data.value = tempArray.toString();
        // if group than run valid based on group min number
        if (includes(data.mods, GROUP)) {
          const min = data.exp || 1;
          valid = tempArray.length >= min;
        }
      } else {
        if (data.required) valid = !!value.trim();
        // only run validation check if valid and has value
        if (valid && value) {
          // see if there is a date format
          const format = data.mods.filter(
            (val) => dateFormats.indexOf(val) !== -1
          )[0];
          for (let type of data.mods) {
            // check if mod is a validation function
            if (typeof validate[type] === "function") {
              // if it is a date then do isDate; otherwise do matching function
              valid =
                type === "date" ? isDate(value, format) : validate[type](value);
              break;
            }
          }
          if (data.exp === false) valid = false;
        }
      }
      data.valid = valid;

      // update with new data
      tempData[name] = data;
      formData.set(form, tempData);
    }

    if (triggerErrorMsg) toggleError(field, data.valid);
    return data;
  }

  /* -------------------------------------------------------------------------- */
  /*                          Validate Magic Function                           */
  /* -------------------------------------------------------------------------- */
  let validateMagic: Record<string, any> = {};
  // Display reactive formData
  validateMagic.data = (el) => getData(el);
  validateMagic.formData = (el) => formData.get(getForm(getEl(el)));
  validateMagic.value = (el) => {
    const data = getData(el);
    if (Array.isArray(data)) {
      return data.reduce((result, item) => {
        result[item.name] = item.value;
        return result;
      }, {});
    }
    // if not array than assume it's a since field so just return the value
    return data && data.value;
  };

  // add or update formData
  validateMagic.updateData = (field, data, triggerErrorMsg) =>
    updateFormData(getEl(field), data, triggerErrorMsg);
  // toggle error message
  validateMagic.toggleError = (field, valid) =>
    toggleError(getEl(field), valid);

  // Check if form is completed and prevent default if not
  // TODO: focus on invalid
  validateMagic.submit = (e) => {
    let invalid = 0;
    getData(e.target).forEach((val) => {
      if (val.valid === false) {
        invalid++;
        // focus on first invalid field
        if (invalid === 1) val.node.focus();
        toggleError(val.node, false);
        e.preventDefault();
        // eslint-disable-next-line no-console -- this error helps with submit and is the only one that should stay in production.
        console.error(`${val.name} ${REQUIRED}`);
      }
    });
  };

  // isComplete works for the form as a whole and fieldsets using either the node itself or the id
  validateMagic.isComplete = (el) => {
    const data = getData(el);
    // if this is array then data is form or fieldset
    return Array.isArray(data)
      ? !data.some((val) => !val.valid)
      : data && data.valid;
  };

  // Add validate functions to validateMagic object
  Object.keys(validate).forEach(
    (key) => (validateMagic = { ...validateMagic, [key]: validate[key] })
  );

  // Main $validate magic function
  Alpine.magic(PLUGIN_NAME, () => validateMagic);
  // $formData magic function
  Alpine.magic("formData", (el) => formData.get(getForm(getEl(el))));

  /* -------------------------------------------------------------------------- */
  /*                            x-required directive                            */
  /* -------------------------------------------------------------------------- */

  Alpine.directive(
    REQUIRED,
    (
      el,
      {
        // modifiers,
        value,
        expression,
      },
      { evaluate }
    ) => {
      // only run if has expression
      if (expression) {
        // Alpine effect watches values for changes
        Alpine.effect(() => {
          const evalExp = evaluate(expression);
          // if it has value than use that as the field name to test; otherwise evaluate the expression
          const required = value ? getData(value)?.value === evalExp : evalExp;
          updateFormData(el, { required: required });
          // hide error message if not required
          if (!required) toggleError(el, true);
        });
      }
    }
  );

  /* -------------------------------------------------------------------------- */
  /*                            x-validate directive                            */
  /* -------------------------------------------------------------------------- */

  Alpine.directive(
    PLUGIN_NAME,
    (el, { modifiers, expression }, { evaluate, cleanup }) => {
      /* -------------------------------------------------------------------------- */
      /*                  Directive Specific Helper Functions                       */
      /* -------------------------------------------------------------------------- */

      const form = getForm(el);

      const defaultData = (field) => {
        const parentNode =
          field.closest(".field-parent") || includes(modifiers, GROUP)
            ? field.parentNode.parentNode
            : field.parentNode;
        return {
          mods: [...modifiers, field.type],
          set: field.closest(FIELDSET),
          parentNode: parentNode,
          exp: expression && evaluate(expression),
        };
      };

      function addEvents(field) {
        // TODO: only add error message on required or validated fields
        addErrorMsg(field);
        const isClickField = includes([CHECKBOX, RADIO, "range"], field.type);
        const eventType = isClickField
          ? "click"
          : isHtmlElement(field, "select")
            ? "change"
            : "blur";
        addEvent(field, eventType, checkIfValid);
        if (includes(modifiers, INPUT) && !isClickField)
          addEvent(field, INPUT, checkIfValid);

        // Hidden inputs inside comboboxes never receive native events when their
        // value changes (Alpine updates the value via reactive binding). Listen to
        // the combobox's custom events instead.
        //
        // Validity is checked against the search input (the visible text field),
        // not the hidden input, because that is the element the validation plugin
        // registered events on and whose key maps to the error message node in
        // the DOM. The combobox already updates searchInput.value before
        // dispatching combobox:change, so its value is correct at handler time.
        const comboboxEl =
          field.type === HIDDEN ? field.closest(".clio-ui-combobox") : null;
        if (comboboxEl) {
          const searchInput = comboboxEl.querySelector("input[type='text']");
          if (searchInput) {
            addEvent(comboboxEl, "combobox:change", (e) => {
              field.value = e.detail.value;
              checkIfValid.call(searchInput, e);
            });
            addEvent(comboboxEl, "combobox:clear", (e) => {
              field.value = "";
              checkIfValid.call(searchInput, e);
            });
          }
        }
      }

      /* -------------------------------------------------------------------------- */
      /*                 If x-validate on <form> validate all fields                */
      /* -------------------------------------------------------------------------- */

      if (isHtmlElement(el, FORM)) {
        // el is form
        // disable in-browser validation
        el.setAttribute("novalidate", true);
        // save all form modifiers
        formModifiers.set(form, modifiers);

        // Only set up observer if one doesn't exist
        if (!formObservers.has(form)) {
          const deleteRemovedNodes = (data, nodes) => {
            // remove nodes that are no longer in the DOM
            const removedNodes = Array.prototype.slice.call(nodes);
            const removedFields = removedNodes.flatMap((node) => {
              // If the node itself is a form element, include it
              const directMatch = isHtmlElement(node, FIELD_SELECTOR)
                ? [node]
                : [];
              // If the node itself is an html element, find all nested form elements
              const nestedMatches = isHtmlElement(node)
                ? Array.from(node.querySelectorAll(FIELD_SELECTOR))
                : [];
              return [...directMatch, ...nestedMatches];
            });

            removedFields.forEach((field) => {
              const fieldName = getName(field);
              if (fieldName && data[fieldName]) {
                delete data[fieldName];
              }
            });
          };

          const onMutate = (mutations) => {
            let data = formData.get(form);
            if (!data) return;

            mutations.forEach(({ removedNodes }) => {
              if (removedNodes?.length > 0) {
                deleteRemovedNodes(data, removedNodes);
              }
            });

            formData.set(form, data);
          };

          const observer = new MutationObserver(onMutate);

          observer.observe(form, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["required"],
          });

          formObservers.set(form, observer);

          cleanup(() => {
            cleanupForm(form);
          });
        }

        const fields = el.querySelectorAll(FIELD_SELECTOR);

        // bind reset with resetting all formData
        addEvent(el, "reset", () => {
          el.reset();
          const data = getData(el);
          // need a short delay for reset to take effect and reread values
          setTimeout(() => {
            data.forEach((field) => updateFormData(field.node));
          }, 50);
        });

        fields.forEach((field) => {
          if (getName(field)) {
            updateFormData(field, defaultData(field));
            // Don't add events or error msgs if it doesn't have a name/id or has x-validate on it so we aren't duplicating function
            // TODO: somehow detect if this is a group of checkboxes or radio buttons with required. Might need to run a forEach twice?
            if (
              !field
                .getAttributeNames()
                .some((attr) => attr.includes(`x-${PLUGIN_NAME}`))
            ) {
              addEvents(field);
            }
          }
        });
      }

      /* -------------------------------------------------------------------------- */
      /*      If x-validate on input, select, or textarea validate this field       */
      /* -------------------------------------------------------------------------- */

      // Only add if has name/id and and is field
      if (getName(el) && isHtmlElement(el, FIELD_SELECTOR)) {
        const formMods = formModifiers.has(form) ? formModifiers.get(form) : [];
        // include form level modifiers so they are also referenced
        modifiers = [...modifiers, ...formMods];
        // el is field element
        updateFormData(el, defaultData(el));
        addEvents(el);
      }

      /* -------------------------------------------------------------------------- */
      /*                           Check Validity Function                          */
      /* -------------------------------------------------------------------------- */

      function checkIfValid(e) {
        const field = this;
        const data = getData(field);
        if (!data) return;

        const mods = data.mods;

        /* --- Update formData with value and expression and trigger error message --- */
        const updatedData = updateFormData(
          field,
          { exp: expression && evaluate(expression) },
          true
        );

        // add input event to blur events once it fails the first time

        if (
          !updatedData.valid &&
          !includes(mods, "bluronly") &&
          e.type === "blur"
        ) {
          addEvent(field, INPUT, checkIfValid);
        }
        // refocus if modifier is enabled
        if (!updatedData.valid && includes(mods, "refocus")) field.focus();

        return updatedData.valid;
      }
    }
  );
  /* ------------------------- End Validate Directive ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                            Toggle Error Message                            */
  /* -------------------------------------------------------------------------- */

  function toggleError(field, valid) {
    if (isCollectionWrapper(field) || isMultiSelectWrapper(field)) {
      return;
    }

    const name = getName(field);

    const parentNode = getData(field).parentNode;

    const errorMsgNode = getEl(`${ERROR_MSG_CLASS}-${name}`);

    /* ---------------------------- Set aria-invalid ---------------------------- */
    setAttr(field, "aria-invalid", !valid);

    // addErrorMsg may silently skip fields whose companion error span isn't
    // reachable from findErrorMsgNode, leaving no node to toggle.
    if (!errorMsgNode) return;

    /* ------------------ Check valid and set and remove error ------------------ */
    if (valid) {
      setAttr(errorMsgNode, HIDDEN);
      parentNode.removeAttribute(DATA_ERROR);
      // hideErrorMsg()
    } else {
      errorMsgNode.removeAttribute(HIDDEN);
      setAttr(parentNode, DATA_ERROR, errorMsgNode.textContent);
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                        Set Up Error Msg Node in DOM                        */
  /* -------------------------------------------------------------------------- */

  /* ----------------- Helper function to find error msg node ----------------- */

  function findErrorMsgNode(el) {
    // perform specific checks for known component structures and their relative position to the error message node
    if (isCheckboxWrapper(el)) {
      let errorNode = el.parentNode.querySelector(`.${ERROR_MSG_CLASS}`);
      return errorNode ?? false;
    }

    if (isAutoCompleteWrapper(el)) {
      let errorNode = findSiblingWithClass(
        el.parentNode.parentNode,
        ERROR_MSG_CLASS
      );
      return errorNode ?? false;
    }

    if (isComboboxWrapper(el)) {
      let errorNode = findSiblingWithClass(
        el.parentNode.parentNode,
        ERROR_MSG_CLASS
      );
      return errorNode ?? false;
    }

    // If we have gotten here, we are assuming that the error message node is a sibling of el
    while (el) {
      // jump to next sibling element
      el = el.nextElementSibling;

      // return el if matches class name
      if (isHtmlElement(el, `.${ERROR_MSG_CLASS}`)) return el;

      // Stop searching if it hits another field
      if (isHtmlElement(el, FIELD_SELECTOR)) return false;
    }
    return false;
  }

  /* ------ Function to setup errorMsgNode by finding it or creating one ------ */

  function addErrorMsg(field) {
    const name = getName(field);

    // Specific fields should not be validated so we return early
    const FIELD_NAMES_TO_SKIP = ["authenticity_token"];

    if (FIELD_NAMES_TO_SKIP.includes(name)) {
      return;
    }

    const errorMsgId = `${ERROR_MSG_CLASS}-${name}`;

    const fieldData = getData(field);

    // set targetNode. The span.error-msg typically appears after the field but groups assign it to set after the wrapper
    let targetNode = includes(fieldData.mods, GROUP)
      ? fieldData.parentNode
      : field;

    // If the targetNode is a checkbox (single)
    // the targetNode is currently a hidden element used by simple form to track empty selections
    // In this case, we want to grab the error message from the nextSibling, which is the visible
    // input type="checkbox"

    let dataErrorMessage = getAttr(targetNode, DATA_ERROR_MSG);
    if (isCheckboxWrapper(targetNode)) {
      dataErrorMessage = getAttr(targetNode.nextSibling, DATA_ERROR_MSG);
    } else if (isAutoCompleteWrapper(targetNode)) {
      dataErrorMessage = getAttr(targetNode, DATA_ERROR_MSG);
    } else if (
      isCollectionWrapper(targetNode) ||
      isMultiSelectWrapper(targetNode)
    ) {
      // currently not supported
      return;
    }

    /* --------------------- Find or Make Error Message Node -------------------- */

    const errorMsgNode = findErrorMsgNode(targetNode);

    if (errorMsgNode) {
      // add id tag and hidden attribute
      setAttr(errorMsgNode, "id", errorMsgId);
      setAttr(errorMsgNode, HIDDEN);

      // add error text if it isn't already there
      if (!errorMsgNode.innerHTML) {
        errorMsgNode.textContent = dataErrorMessage;
      }

      // Add aria-errormessage using the ID to field
      setAttr(field, "aria-errormessage", errorMsgId);

      //  Only add element if it does not yet exist
      if (!getEl(errorMsgId)) targetNode.after(errorMsgNode);
    }
  }
}
